/**
 * StartTestPicker — 24 × 7 entry point into the Hearing Tests interface.
 *
 * Product rule (Feb 17 2026, per user directive):
 *   • Working-hours restrictions apply ONLY to new appointment creation.
 *   • Every clinical test tab (Pure Tone, Speech, Impedance, OAE, ABR…)
 *     MUST be reachable at any time — including outside clinic hours,
 *     on holidays, on Sundays — by Audiologist / Clinic Owner / Manager.
 *
 * Root cause of the reported block: the diagnostics Kanban queue is
 * IST-today-scoped, so a patient whose appointment is on a different
 * day (e.g. tomorrow) has no card — the audiologist could not click
 * their way into the test tabs. This picker fills that gap.
 *
 * Flow:
 *   1. Audiologist searches an existing patient by name / mobile / MRD.
 *   2. Click → POST /api/diagnostics/queue/start with just `patient_id`.
 *      Backend creates a fresh session (no appointment / no token / no
 *      hours guard — verified in `routers/diagnostics_queue.py`).
 *   3. Set `activeTest` in the TestContext + navigate('/test').
 *      TestProceduresModule immediately shows the PTA / Speech / Impedance
 *      tabs — no clinic-hours gate anywhere in this path.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { X, Search, Loader2, Play, Users } from 'lucide-react';
import { useTestContext } from '../../TestContext';

import useEscapeKey from '../../hooks/useEscapeKey';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initials = (n = '') =>
  (n || '?').trim().split(/\s+/).slice(0, 2).map((s) => s[0] || '').join('').toUpperCase();

export default function StartTestPicker({ open, onClose, targetTab = null }) {
  const navigate = useNavigate();
  const { setActiveTest } = useTestContext();
  useEscapeKey(onClose);

  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(null);
  const [err, setErr] = useState('');
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Debounced search — 250 ms after last keystroke.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const params = q.trim() ? { search: q.trim(), limit: 20 } : { limit: 20 };
        const r = await axios.get(`${API}/patients`, { params });
        const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
        setRows(arr);
        setErr('');
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Could not load patients');
      } finally { setLoading(false); }
    }, 250);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, open]);

  // Auto-focus the search input when the modal opens.
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 30);
  }, [open]);

  const handlePick = async (p) => {
    if (!p?.patient_id || starting) return;
    setStarting(p.patient_id); setErr('');
    try {
      const r = await axios.post(`${API}/diagnostics/queue/start`, {
        patient_id: p.patient_id,
      });
      setActiveTest({
        patient: r.data?.patient || p,
        sessionId: r.data?.session_id,
      });
      onClose?.();
      // Deep-link into the requested tab (Available-Tests launcher tile
      // click flow) — otherwise land on the default Pre-Test tab.
      navigate(targetTab ? `/test?tab=${targetTab}` : '/test');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not start test session');
    } finally { setStarting(null); }
  };

  const empty = useMemo(() => !loading && rows.length === 0, [loading, rows]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 flex items-start justify-center p-4 pt-16"
      data-testid="start-test-picker-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl flex flex-col max-h-[80vh]">
        <header className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
              <Play size={15} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-900">Start Test</h2>
              <div className="text-[11px] text-slate-500">
                Pick any existing patient — works 24 × 7, no appointment required
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="start-test-picker-close"
            className="w-7 h-7 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500"
          >
            <X size={16} />
          </button>
        </header>

        <div className="px-5 py-3 border-b border-slate-100">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              ref={inputRef}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="start-test-picker-search"
              placeholder="Search by name, mobile, or MRD…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-300"
            />
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center py-10">
              <Loader2 size={18} className="animate-spin text-slate-400" />
            </div>
          )}
          {!loading && err && (
            <div
              className="px-5 py-3 text-xs font-semibold text-rose-700 bg-rose-50 border-b border-rose-100"
              data-testid="start-test-picker-error"
            >{err}</div>
          )}
          {!loading && empty && !err && (
            <div className="p-10 text-center">
              <Users size={26} className="mx-auto text-slate-300 mb-2" />
              <div className="text-sm font-semibold text-slate-700">
                {q ? 'No matching patient' : 'No patients yet'}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {q ? 'Try a different name, mobile, or MRD.'
                  : 'Register a patient first via Front Desk → Walk-in.'}
              </div>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <ul className="divide-y divide-slate-100">
              {rows.map((p, i) => (
                <li
                  key={p.patient_id}
                  data-testid={`start-test-picker-row-${i}`}
                  className="px-5 py-3 hover:bg-indigo-50/40 cursor-pointer flex items-center gap-3"
                  onClick={() => handlePick(p)}
                >
                  <span className="w-9 h-9 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-white flex items-center justify-center text-[11px] font-black flex-shrink-0">
                    {initials(p.name)}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">
                      {p.name || '—'}
                    </div>
                    <div className="text-[11px] text-slate-500 truncate">
                      {p.gender || '—'} · {p.age ? `${p.age}y` : '—'}
                      {p.mobile ? ` · ${p.mobile}` : ''}
                      {(p.mrd || p.patient_id) ? ` · MRD ${p.mrd || p.patient_id}` : ''}
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={starting === p.patient_id}
                    onClick={(e) => { e.stopPropagation(); handlePick(p); }}
                    data-testid={`start-test-picker-start-${i}`}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1.5 rounded bg-indigo-600 hover:bg-indigo-700 text-white disabled:opacity-40 shrink-0"
                  >
                    {starting === p.patient_id ? (
                      <><Loader2 size={11} className="animate-spin" /> Starting…</>
                    ) : (
                      <><Play size={11} /> Start</>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="px-5 py-2.5 border-t border-slate-100 text-[10px] text-slate-400">
          Tip: creating a new session here does NOT block on clinic hours — test tabs stay open 24 × 7.
        </footer>
      </div>
    </div>
  );
}
